import Phaser from 'phaser';
import type { Direction } from '../types';
import { controllerManager } from './ControllerSystem';
import { MovementCommandQueue } from './MovementCommandQueue';

export class InputSystem {
  private cursors: Phaser.Types.Input.Keyboard.CursorKeys;
  private keys: Record<'up' | 'down' | 'left' | 'right', Phaser.Input.Keyboard.Key>;
  private touchDirection?: Direction;
  private travelDirection?: Direction;
  private readonly commandQueue = new MovementCommandQueue(270);
  private nextMoveAt = 0;
  private detachPhaserGamepad?: () => void;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly onMove: (direction: Direction) => boolean,
    private readonly getMoveDelay: () => number,
  ) {
    if (!scene.input.keyboard) throw new Error('Keyboard input is unavailable.');
    this.cursors = scene.input.keyboard.createCursorKeys();
    this.keys = scene.input.keyboard.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.W,
      down: Phaser.Input.Keyboard.KeyCodes.S,
      left: Phaser.Input.Keyboard.KeyCodes.A,
      right: Phaser.Input.Keyboard.KeyCodes.D,
    }) as Record<'up' | 'down' | 'left' | 'right', Phaser.Input.Keyboard.Key>;
    window.addEventListener('packet-direction', this.handleTouch as EventListener);
    window.addEventListener('packet-direction-end', this.handleTouchEnd);
    scene.input.keyboard.on('keydown', this.handleKeyDown);
    this.detachPhaserGamepad = controllerManager.attachPhaser(scene.input.gamepad);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  update(time: number): void {
    const heldDirection = this.touchDirection ?? this.keyboardDirection() ?? controllerManager.getMovementDirection();
    if (heldDirection && heldDirection !== this.travelDirection) this.commandQueue.request(heldDirection, time);
    if (time < this.nextMoveAt) return;

    const queued = this.commandQueue.peek(time);
    if (queued && this.onMove(queued)) {
      this.commandQueue.consume(time);
      this.travelDirection = queued;
      this.nextMoveAt = time + this.getMoveDelay();
      return;
    }

    if (this.travelDirection && (heldDirection || queued) && this.onMove(this.travelDirection)) {
      this.nextMoveAt = time + this.getMoveDelay();
      return;
    }

    if (heldDirection && this.onMove(heldDirection)) {
      this.travelDirection = heldDirection;
      this.nextMoveAt = time + this.getMoveDelay();
    }
  }

  private keyboardDirection(): Direction | undefined {
    if (this.cursors.up.isDown || this.keys.up.isDown) return 'up';
    if (this.cursors.down.isDown || this.keys.down.isDown) return 'down';
    if (this.cursors.left.isDown || this.keys.left.isDown) return 'left';
    if (this.cursors.right.isDown || this.keys.right.isDown) return 'right';
    return undefined;
  }

  private readonly handleTouch = (event: CustomEvent<Direction>): void => {
    controllerManager.noteInput('touch');
    this.touchDirection = event.detail;
    this.commandQueue.request(event.detail, this.scene.time.now);
  };

  private readonly handleTouchEnd = (): void => {
    this.touchDirection = undefined;
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    controllerManager.noteInput('keyboard');
    const directions: Record<string, Direction | undefined> = {
      ArrowUp: 'up', KeyW: 'up', ArrowDown: 'down', KeyS: 'down',
      ArrowLeft: 'left', KeyA: 'left', ArrowRight: 'right', KeyD: 'right',
    };
    const direction = directions[event.code];
    if (!direction) return;
    this.commandQueue.request(direction, this.scene.time.now);
  };

  private destroy(): void {
    window.removeEventListener('packet-direction', this.handleTouch as EventListener);
    window.removeEventListener('packet-direction-end', this.handleTouchEnd);
    this.scene.input.keyboard?.off('keydown', this.handleKeyDown);
    this.detachPhaserGamepad?.();
    this.commandQueue.clear();
  }
}
